// 이벤트 큐 — 모아서 보내고, 실패하면 간격을 늘려 가며 정해진 횟수까지 다시 보내고, 그래도 안 되면 버린다.
// 어떤 경로로도 던지지 않는다 — 수집 실패가 화면 동작을 깨면 안 된다 (prd/103 AC-031)
import { checkEvent, type RuleViolation } from '../domain/event-rules';
import type { CollectedEvent, EventSink } from '../ports/event-sink';

/** 서버 한 번에 받는 상한 (api/internal/events.MaxBatch) — beacon 도 이 단위로 쪼갠다 */
export const SERVER_MAX_BATCH = 100;
const MAX_BACKOFF_MS = 60_000;

export interface TrackerOptions {
  readonly sink: EventSink;
  readonly anonymousId: string;
  readonly batchSize: number;
  readonly flushIntervalMs: number;
  readonly maxAttempts: number;
  readonly appVersion?: string;
  readonly newId?: () => string;
  readonly now?: () => Date;
  /** 규칙 위반·재시도 소진으로 버린 이벤트 알림 (개발 중 확인용) */
  readonly onDrop?: (reason: RuleViolation | 'retry-exhausted', name: string) => void;
}

export interface Tracker {
  enqueue(name: string, props: Readonly<Record<string, unknown>>): void;
  /** 지금 쌓인 것을 한 배치 보낸다 */
  flush(): Promise<void>;
  /** 탭을 닫을 때 — 남은 것을 batchSize 단위 beacon 으로 맡긴다 */
  flushOnExit(): void;
  stop(): void;
  readonly pending: number;
}

interface Queued {
  readonly event: CollectedEvent;
  attempts: number;
}

export function createTracker(options: TrackerOptions): Tracker {
  const newId = options.newId ?? (() => crypto.randomUUID());
  const now = options.now ?? (() => new Date());
  const batchSize = Math.min(options.batchSize, SERVER_MAX_BATCH);
  const queue: Queued[] = [];
  let timer: ReturnType<typeof setTimeout> | null = null;
  let inFlight = false;
  let stopped = false;
  let failures = 0; // 연속 실패 — 다음 시도 간격을 늘린다

  const schedule = (delay: number) => {
    if (timer !== null || stopped || queue.length === 0) return;
    timer = setTimeout(() => {
      timer = null;
      void flush();
    }, delay);
  };

  async function flush(): Promise<void> {
    if (inFlight || stopped || queue.length === 0) return;
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    inFlight = true;
    const batch = queue.splice(0, batchSize);
    let ok = false;
    try {
      await options.sink.send(batch.map(q => q.event));
      ok = true;
      failures = 0;
    } catch {
      failures += 1;
      // 다시 보낼 것은 앞으로 되돌린다 — 같은 clientEventId 라 서버에서 중복이 생기지 않는다 (AC-032)
      const retry = batch.filter(q => {
        q.attempts += 1;
        if (q.attempts < options.maxAttempts) return true;
        options.onDrop?.('retry-exhausted', q.event.name);
        return false;
      });
      queue.unshift(...retry);
    } finally {
      inFlight = false;
    }
    // 성공했고 한 배치 이상 밀려 있으면 바로 이어서, 실패했으면 간격을 2배씩 늘려 다시 (최대 60초)
    if (ok && queue.length >= batchSize) void flush();
    else schedule(ok ? options.flushIntervalMs : Math.min(options.flushIntervalMs * 2 ** failures, MAX_BACKOFF_MS));
  }

  return {
    get pending() {
      return queue.length;
    },
    enqueue(name, props) {
      if (stopped) return;
      try {
        const violation = checkEvent(name, props);
        if (violation) {
          options.onDrop?.(violation, name);
          return;
        }
        queue.push({
          event: {
            clientEventId: newId(),
            name,
            anonymousId: options.anonymousId,
            occurredAt: now().toISOString(),
            props,
            ...(options.appVersion ? { appVersion: options.appVersion } : {}),
          },
          attempts: 0,
        });
        if (queue.length >= batchSize) void flush();
        else schedule(options.flushIntervalMs);
      } catch {
        // 직렬화 불가 props 등 — 이벤트 하나를 버리고 화면은 계속 간다
        options.onDrop?.('size', name);
      }
    },
    flush,
    flushOnExit() {
      // 서버 상한·beacon 크기 한도를 넘지 않게 batchSize 단위로 맡기고, 맡긴 덩어리만 큐에서 지운다
      while (queue.length > 0) {
        const chunk = queue.slice(0, batchSize);
        let accepted = false;
        try {
          accepted = options.sink.beacon(chunk.map(q => q.event));
        } catch {
          // silent-ok: 탭을 닫는 중이라 알릴 곳이 없다. 명세상 유실은 허용, 중복은 clientEventId 가 막는다
        }
        if (!accepted) return;
        queue.splice(0, chunk.length);
      }
    },
    stop() {
      stopped = true;
      if (timer !== null) clearTimeout(timer);
      timer = null;
    },
  };
}
